import { defineStore } from 'pinia'
import { v4 as uuidv4 } from 'uuid'
import { addModelFile, getModelFile, deleteModelFile } from '../utils/model3dDB.js'
import { extractGlbLights, H3D_LIGHTS_METADATA_VERSION } from '../3d/h3dLights.js'
import { DEFAULT_LIGHTING_QUALITY } from '../3d/h3dLightingQuality.js'

const STORAGE_KEY = 'hasp-model3d-library'

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

export const useModel3dStore = defineStore('model3d', {
  state: () => ({
    models: loadFromStorage(),
  }),
  getters: {
    all: (state) => state.models,
  },
  actions: {
    _persist() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.models))
      } catch (e) {
        console.warn('Failed to persist 3D model library:', e)
      }
    },

    async importGlbFile(file, { name, description = '' } = {}) {
      if (!/\.glb$/i.test(file.name)) {
        throw new Error('Only .glb files are supported.')
      }
      const data = await readFileAsArrayBuffer(file)
      const storageId = await addModelFile(data, file.name, file.type || 'model/gltf-binary')
      const model = {
        uuid: uuidv4(),
        name: name || file.name.replace(/\.glb$/i, ''),
        description,
        filename: file.name,
        resourceType: 'model3d',
        type: 'glb',
        storageId,
        originalFilename: file.name,
        // Stamped on import only. A resource saved before this setting existed keeps
        // compiling as it always did - see resolveLightingQuality().
        lightingQuality: DEFAULT_LIGHTING_QUALITY,
        metadata: {
          fileSize: file.size,
          lights: extractGlbLights(data),
          lightsVersion: H3D_LIGHTS_METADATA_VERSION,
        },
      }
      this.models.push(model)
      this._persist()
      return model.uuid
    },

    removeModel(uuid) {
      const model = this.getModel(uuid)
      if (!model) return
      this.models.splice(this.models.indexOf(model), 1)
      this._persist()
      const stillReferenced = model.storageId != null && this.models.some(m => m.storageId === model.storageId)
      if (model.storageId != null && !stillReferenced) deleteModelFile(model.storageId)
    },

    duplicateModel(uuid) {
      const src = this.getModel(uuid)
      if (!src) return null
      const copy = {
        ...JSON.parse(JSON.stringify(src)),
        uuid: uuidv4(),
        name: src.name + ' (copy)',
      }
      this.models.push(copy)
      this._persist()
      return copy.uuid
    },

    getModel(uuid) {
      return this.models.find(m => m.uuid === uuid) ?? null
    },

    updateModel(uuid, patch) {
      const model = this.getModel(uuid)
      if (!model) return
      Object.assign(model, patch)
      this._persist()
    },

    async getModelArrayBuffer(uuid) {
      const model = this.getModel(uuid)
      if (!model || model.storageId == null) return null
      const record = await getModelFile(model.storageId)
      return record?.data ?? null
    },

    /**
     * Cached lights, or null when this model has none cached or they were derived by an
     * older id strategy — in both cases the GLB has to be analysed again.
     */
    getModelLights(uuid) {
      const metadata = this.getModel(uuid)?.metadata
      if (!Array.isArray(metadata?.lights)) return null
      return metadata.lightsVersion === H3D_LIGHTS_METADATA_VERSION ? metadata.lights : null
    },

    /** Resolves the light list, re-analysing the stored GLB when the cache is outdated. */
    async ensureModelLights(uuid) {
      const model = this.getModel(uuid)
      if (!model) return []
      const cached = this.getModelLights(uuid)
      if (cached) return cached

      const data = await this.getModelArrayBuffer(uuid)
      // Leave the stale cache alone if the file is gone: it is the only record left, and
      // re-analysis must still happen once the resource is available again.
      if (!data) return []

      const lights = extractGlbLights(data)
      model.metadata = {
        ...(model.metadata ?? {}),
        lights,
        lightsVersion: H3D_LIGHTS_METADATA_VERSION,
      }
      this._persist()
      return lights
    },
  },
})

function readFileAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error)
    reader.readAsArrayBuffer(file)
  })
}
